import {
    Activity,
    Calendar,
    ClipboardList,
    LayoutDashboard,
    Shield,
    User,
    UserCheck,
    Users,
    Bell,
    Clock,
    AlertCircle,
    CheckCircle,
    XCircle,
    ArrowRight,
    Eye
} from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CountBadge from '../countdown/CountBase';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const NotificationPopup = ({ complaints, isOpen, setIsOpen, markAsRead }) => {
    const navigate = useNavigate();

    // Get status color and icon
    const getStatusConfig = (status) => {
        const configs = {
            'pending': {
                icon: <Clock className="w-3.5 h-3.5" />,
                color: 'bg-amber-100 text-amber-600',
                dotColor: 'bg-amber-500'
            },
            'in-operation': {
                icon: <Activity className="w-3.5 h-3.5" />,
                color: 'bg-blue-100 text-blue-600',
                dotColor: 'bg-blue-500'
            },
            'paused': {
                icon: <AlertCircle className="w-3.5 h-3.5" />,
                color: 'bg-gray-100 text-gray-600',
                dotColor: 'bg-gray-500'
            },
            'completed': {
                icon: <CheckCircle className="w-3.5 h-3.5" />,
                color: 'bg-emerald-100 text-emerald-600',
                dotColor: 'bg-emerald-500'
            }
        };
        return configs[status] || configs['pending'];
    };

    // Get complaint type icon
    const getComplaintTypeIcon = (type) => {
        const icons = {
            'Red signal': <AlertCircle className="w-4 h-4 text-red-500" />,
            'Internet slow': <Activity className="w-4 h-4 text-yellow-500" />,
            'No internet': <XCircle className="w-4 h-4 text-red-500" />,
            'Other': <ClipboardList className="w-4 h-4 text-blue-500" />
        };
        return icons[type] || <ClipboardList className="w-4 h-4 text-gray-500" />;
    };

    // Handle notification click
    const handleNotificationClick = (complaintId) => {
        setIsOpen(false);
        if (markAsRead) {
            markAsRead(complaintId);
        }
        // navigate(`/complaint-status/${complaintId}`);
    };

    // Get time ago
    const getTimeAgo = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate?.() || new Date(timestamp);
        return dayjs(date).fromNow();
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-40"
                onClick={() => setIsOpen(false)}
            />

            <div className="absolute right-0 top-14 w-[380px] max-w-[90vw] bg-white border border-gray-200 rounded-2xl shadow-2xl py-2 z-50 animate-fade-in-down max-h-[80vh] flex flex-col">

                {/* Header */}
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 bg-blue-50 rounded-lg">
                            <Bell className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="font-semibold text-gray-800">Notifications</span>
                        {complaints && complaints.length > 0 && (
                            <span className="bg-blue-100 text-blue-700 text-[10px] font-medium px-2 py-0.5 rounded-full">
                                {complaints.length}
                            </span>
                        )}
                    </div>
                    {complaints && complaints.length > 0 && markAsRead && (
                        <button
                            onClick={() => {
                                complaints.forEach(c => markAsRead(c.id));
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium transition"
                        >
                            Mark all read
                        </button>
                    )}
                </div>

                {/* Notifications List */}
                <div className="overflow-y-auto flex-1">
                    {complaints && complaints.length > 0 ? (
                        <div className="py-1">
                            {complaints.map((com, index) => {
                                const statusConfig = getStatusConfig(com.status);
                                const isDelayed = com.isDelayed || false;

                                return (
                                    <div
                                        key={com.id || index}
                                        onClick={() => handleNotificationClick(com.id)}
                                        className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-50 last:border-0 ${!com.isRead ? 'bg-blue-50/30 hover:bg-blue-50/50' : ''
                                            }`}
                                    >
                                        {/* Unread indicator */}
                                        {!com.isRead && (
                                            <div className="flex-shrink-0 mt-1">
                                                <span className="w-2 h-2 bg-blue-600 rounded-full block"></span>
                                            </div>
                                        )}

                                        {/* Icon */}
                                        <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${statusConfig.color}`}>
                                            {getComplaintTypeIcon(com.complaintType)}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className="text-sm font-medium text-gray-800 truncate">
                                                    {com.ticketNumber}
                                                    {isDelayed && (
                                                        <span className="ml-1.5 text-[10px] bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded-full font-medium">
                                                            Delayed
                                                        </span>
                                                    )}
                                                </p>
                                                <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
                                                    {getTimeAgo(com.updatedAt || com.createdAt)}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 truncate">
                                                {com.customerInfo?.name || 'Unknown Customer'}
                                            </p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium ${statusConfig.color}`}>
                                                    {statusConfig.icon}
                                                    {com.status}
                                                </span>
                                                {com.category && (
                                                    <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                                                        {com.category}
                                                    </span>
                                                )}
                                            </div>
                                            {com.description && (
                                                <p className="text-xs text-gray-400 truncate mt-0.5">
                                                    {com.description}
                                                </p>
                                            )}
                                        </div>

                                        {/* Arrow */}
                                        <div className="flex-shrink-0 self-center ml-1">
                                            <ArrowRight className="w-3.5 h-3.5 text-gray-300" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 px-4">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                <Bell className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-600 font-medium">No notifications</p>
                            <p className="text-sm text-gray-400 mt-1">You're all caught up!</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-100 px-4 py-2.5 flex-shrink-0">
                    <Link
                        to="/my-complaints"
                        className="flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors font-medium py-1.5 rounded-lg hover:bg-gray-50"
                        onClick={() => setIsOpen(false)}
                    >
                        <Eye className="w-4 h-4" />
                        View All Complaints
                    </Link>
                </div>
            </div>
        </>
    );
};

export default NotificationPopup;